/**
 * Loan start date for interest calculation — uses createdAt from the API.
 */
export const getLoanStartDate = (loan) => {
  const raw = loan?.createdAt || loan?.createdDate;
  if (!raw) return null;
  const date = new Date(raw);
  return Number.isNaN(date.getTime()) ? null : date;
};

/** Actual record creation timestamp from the API (for ledger / audit display). */
export const getLoanCreatedAtDate = (loan) => {
  const raw = loan?.createdAt;
  if (!raw) return null;
  const date = new Date(raw);
  return Number.isNaN(date.getTime()) ? null : date;
};

/**
 * Due date = loan start + tenure (months).
 */
export const getLoanDueDate = (loan) => {
  const startDate = getLoanStartDate(loan);
  const tenureMonths = parseInt(loan?.tenureMonths || 0, 10);
  if (!startDate || !tenureMonths) return null;

  const dueDate = new Date(startDate);
  dueDate.setMonth(dueDate.getMonth() + tenureMonths);
  return dueDate;
};

/**
 * Inclusive billing months from start through end (minimum 1 month).
 * A new month starts on the same day-of-month as the loan start date
 * (e.g. Jan 13 → Feb 13 = month 2; Aug 6 is still month 7, Aug 13 = month 8).
 */
export const calculateMonthsElapsed = (startDate, endDate = new Date()) => {
  if (!startDate) return 1;

  const start = new Date(startDate);
  const end = new Date(endDate);
  if (Number.isNaN(start.getTime()) || Number.isNaN(end.getTime())) return 1;

  let months = (end.getFullYear() - start.getFullYear()) * 12 + (end.getMonth() - start.getMonth());
  if (end.getDate() >= start.getDate()) {
    months += 1;
  }

  return Math.max(1, months);
};

export const calculateLoanDueAmount = (loan, asOfDate = new Date()) => {
  const advances = Array.isArray(loan?.advances) && loan.advances.length > 0
    ? loan.advances
    : [{
        amount: loan?.finalDisbursedAmount || 0,
        interestRatePerMonth: loan?.interestRatePerMonth || 0,
        advanceDate: loan?.createdAt || loan?.createdDate
      }];
  const advanceDetails = advances.map((advance) => {
    const amount = parseFloat(advance?.amount || 0);
    const rate = parseFloat(advance?.interestRatePerMonth || loan?.interestRatePerMonth || 0);
    const monthlyInterest = Math.round(amount * (rate / 100));
    const monthsElapsed = calculateMonthsElapsed(advance?.advanceDate, asOfDate);
    const tenureMonths = parseInt(loan?.tenureMonths || 0, 10);
    let accruedInterest = 0;
    let normalInterest = 0;
    let overdueInterest = 0;
    let currentMonthlyInterest = monthlyInterest;

    if (tenureMonths > 0) {
      const currentBlock = Math.floor((monthsElapsed - 1) / tenureMonths);
      let accumulatedInterest = 0;
      for (let block = 0; block <= currentBlock; block += 1) {
        const base = block === 0 ? amount : amount + accumulatedInterest;
        const blockMonthlyInterest = block === 0
          ? monthlyInterest
          : Math.round(base * (rate / 100) * 100) / 100;
        const monthsInBlock = block === currentBlock
          ? monthsElapsed - (block * tenureMonths)
          : tenureMonths;
        const blockInterest = blockMonthlyInterest * monthsInBlock;
        accruedInterest += blockInterest;
        if (block === 0) {
          normalInterest += blockInterest;
        } else {
          overdueInterest += blockInterest;
        }
        if (block < currentBlock) {
          accumulatedInterest += blockMonthlyInterest * tenureMonths;
        }
        if (block === currentBlock) {
          currentMonthlyInterest = blockMonthlyInterest;
        }
      }
    } else {
      accruedInterest = monthsElapsed * monthlyInterest;
      normalInterest = accruedInterest;
    }

    return {
      amount,
      monthlyInterest,
      currentMonthlyInterest,
      monthsElapsed,
      normalInterest,
      overdueInterest,
      accruedInterest,
    };
  });
  const principal = advanceDetails.reduce((sum, advance) => sum + advance.amount, 0);
  const monthlyInterest = advanceDetails.reduce((sum, advance) => sum + advance.currentMonthlyInterest, 0);
  const monthsElapsed = advanceDetails.reduce(
    (max, advance) => Math.max(max, advance.monthsElapsed), 1
  );
  const startDate = getLoanStartDate(loan);
  const accruedInterest = advanceDetails.reduce((sum, advance) => sum + advance.accruedInterest, 0);
  const normalInterest = advanceDetails.reduce((sum, advance) => sum + advance.normalInterest, 0);
  const overdueInterest = advanceDetails.reduce((sum, advance) => sum + advance.overdueInterest, 0);

  return {
    principal,
    monthlyInterest,
    monthsElapsed,
    accruedInterest,
    normalInterest,
    overdueInterest,
    totalDue: principal + accruedInterest,
    startDate,
    dueDate: getLoanDueDate(loan),
  };
};
